import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['textarea', 'fileInput', 'copyCheckbox', 'submitButton', 'attachChips', 'errorContainer']
    static values = {
        csrfToken: String,
        sendUrl: String,
        uploadUrl: String,
        removeLabel: String,
        sendErrorLabel: String,
    }
    
    selectedFiles = []
    
    connect() {
        this.bubbleTemplate = document.getElementById('litigant-chat-bubble-template')
    }

    handleFocus() {
        requestAnimationFrame(() => {
            const chatContainer = document.querySelector('[data-litigant-messages--chat-scroll-target="messages"]')
            if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight
            }
        })
    }

    async handleSubmit(event) {
        event.preventDefault()
        
        const message = this.textareaTarget.value.trim()
        const files = [...this.selectedFiles]
        
        if (!message && files.length === 0) {
            return
        }
        
        this.submitButtonTarget.disabled = true
        this.errorContainerTarget.classList.add('d-none')
        this.errorContainerTarget.textContent = ''
        
        try {
            let uploadedAttachments = []
            let messageIds = []
            
            // Suppress realtime echo while our send is in-flight.
            document.dispatchEvent(new CustomEvent('litigant-messages:sending'))
            
            if (files.length > 0) {
                // Route text + files through /upload as one request so the backend emits
                // a single consolidated realtime event (avoids the lawyer-side 5s merge race).
                const uploadResult = await this.uploadFiles(files, message)
                uploadedAttachments = (uploadResult.messages || []).flatMap(
                    msg => msg.file_attachments || []
                )
                messageIds = (uploadResult.messages || []).map(msg => msg.id).filter(Boolean)
                // text_message_id is the consolidated event's id (text row, not an attachment).
                if (uploadResult.text_message_id) {
                    messageIds.push(uploadResult.text_message_id)
                }
            } else if (message) {
                const result = await this.sendMessage(message)
                if (result.id) messageIds.push(result.id)
            }
            
            this.appendBubble(message, uploadedAttachments)
            
            if (messageIds.length > 0) {
                document.dispatchEvent(new CustomEvent('litigant-messages:rendered', {
                    detail: { messageIds },
                }))
            }
            
            this.textareaTarget.value = ''
            // Notify the autosize controller so it shrinks back to the initial height.
            this.textareaTarget.dispatchEvent(new Event('input', { bubbles: true }))
            this.textareaTarget.blur()
            this.selectedFiles = []
            this.renderAttachChips()
            
            const chatContainer = document.querySelector('[data-litigant-messages--chat-scroll-target="messages"]')
            if (chatContainer) {
                chatContainer.dispatchEvent(new CustomEvent('litigant-messages:new-message', { bubbles: true, detail: { local: true } }))
            }
        } catch (error) {
            console.error('Send failed:', error)
            this.errorContainerTarget.textContent = error.message || this.sendErrorLabelValue || "Erreur lors de l'envoi. Veuillez réessayer."
            this.errorContainerTarget.classList.remove('d-none')
            // Server may have persisted+broadcast the message even though our fetch failed;
            // release the suppression flag so the genuine realtime echo renders.
            document.dispatchEvent(new CustomEvent('litigant-messages:send-failed'))
        } finally {
            this.submitButtonTarget.disabled = false
        }
    }
    
    async sendMessage(content) {
        const formData = new FormData()
        formData.append('message', content)
        formData.append('sendCopyToSender', this.hasCopyCheckboxTarget && this.copyCheckboxTarget.checked ? '1' : '0')
        formData.append('_csrf_token', this.csrfTokenValue)
        
        const response = await fetch(this.sendUrlValue, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json'
            }
        })
        
        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            throw new Error(data.error || '')
        }
        
        return response.json()
    }
    
    async uploadFiles(filesToUpload, message = '') {
        const formData = new FormData()
        filesToUpload.forEach(file => formData.append('attachments[]', file))
        formData.append('sendCopyToSender', this.hasCopyCheckboxTarget && this.copyCheckboxTarget.checked ? '1' : '0')
        formData.append('_csrf_token', this.csrfTokenValue)
        if (message) {
            formData.append('message', message)
        }
        
        const response = await fetch(this.uploadUrlValue, {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json'
            }
        })
        
        if (!response.ok) {
            const data = await response.json().catch(() => ({}))
            throw new Error(data.error || '')
        }
        
        return response.json()
    }
    
    appendBubble(textContent, files) {
        if (!this.bubbleTemplate) return
        
        const clone = this.bubbleTemplate.content.cloneNode(true)
        
        const textContainer = clone.querySelector('.litigant-chat__bubble-text')
        if (textContent) {
            textContainer.replaceChildren()
            textContent.split('\n').forEach((line, index) => {
                if (index > 0) {
                    textContainer.appendChild(document.createElement('br'))
                }
                textContainer.appendChild(document.createTextNode(line))
            })
        } else {
            textContainer.classList.add('d-none')
        }
        
        if (files && files.length > 0) {
            const attachmentTemplate = clone.querySelector('.litigant-chat__attachment')
            const contentContainer = clone.querySelector('.litigant-chat__bubble-content')
            
            files.forEach((file, index) => {
                const attachNode = index === 0 ? attachmentTemplate : attachmentTemplate.cloneNode(true)
                attachNode.classList.remove('d-none')
                
                const fileName = file.file_name || file.name || ''
                const extension = file.extension || (fileName.includes('.') ? fileName.split('.').pop() : '')
                
                const linkEl = attachNode.querySelector('.attachment-link')
                if (linkEl && file.preview_url) {
                    linkEl.href = file.preview_url
                }
                
                const nameEl = attachNode.querySelector('.attachment-name')
                if (nameEl) nameEl.textContent = fileName
                
                const extEl = attachNode.querySelector('.attachment-extension')
                if (extEl) extEl.textContent = extension
                
                const sizeEl = attachNode.querySelector('.attachment-size')
                const sizeBytes = typeof file.size === 'number' ? file.size : parseInt(file.size, 10)
                if (sizeEl && Number.isFinite(sizeBytes) && sizeBytes > 0) {
                    const sizeMb = Math.ceil((sizeBytes / 1048576) * 10) / 10
                    sizeEl.textContent = `${sizeMb} MB`
                    sizeEl.classList.remove('d-none')
                }
                
                if (index > 0) {
                    contentContainer.appendChild(attachNode)
                }
            })
        }
        
        const timeEl = clone.querySelector('.time-text')
        const now = new Date()
        timeEl.textContent = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0')
        
        // Stamp createdAt so the realtime controller's bubble-merge logic can evaluate
        // whether an incoming realtime message should merge with our just-sent bubble.
        const rowEl = clone.querySelector('.litigant-chat__row')
        if (rowEl) {
            rowEl.dataset.createdAt = now.toISOString()
        }
        
        const messagesContainer = document.querySelector('.litigant-chat__messages')
        if (messagesContainer) {
            messagesContainer.appendChild(clone)
        }
    }
    
    triggerFileInput() {
        this.fileInputTarget.click()
    }
    
    handleFileSelect(event) {
        const files = Array.from(event.target.files)
        this.selectedFiles.push(...files)
        this.renderAttachChips()
        event.target.value = ''
    }
    
    removeFile(event) {
        const index = parseInt(event.currentTarget.dataset.index, 10)
        this.selectedFiles.splice(index, 1)
        this.renderAttachChips()
    }
    
    renderAttachChips() {
        const removeLabel = this.removeLabelValue || 'Retirer'
        const fragment = document.createDocumentFragment()

        this.selectedFiles.forEach((file, index) => {
            const extension = (file.name.split('.').pop() || '').toLowerCase()
            const sizeMb = (file.size / 1048576).toFixed(1)

            const wrapper = document.createElement('div')
            wrapper.className = 'litigant-chat__pre-attach'

            const card = document.createElement('div')
            card.className = 'litigant-chat__pre-attach-card'

            const ext = document.createElement('div')
            ext.className = 'litigant-chat__pre-attach-ext'
            ext.textContent = `.${extension}`

            const info = document.createElement('div')
            info.className = 'litigant-chat__pre-attach-info'

            const name = document.createElement('p')
            name.className = 'litigant-chat__pre-attach-name fs-14'
            name.textContent = file.name

            const size = document.createElement('span')
            size.className = 'litigant-chat__pre-attach-size fs-14'
            size.textContent = `${sizeMb} Mo`

            info.appendChild(name)
            info.appendChild(size)

            const downloadIcon = document.createElement('i')
            downloadIcon.className = 'uil uil-download-alt litigant-chat__pre-attach-download'

            card.appendChild(ext)
            card.appendChild(info)
            card.appendChild(downloadIcon)

            const removeButton = document.createElement('button')
            removeButton.type = 'button'
            removeButton.className = 'litigant-chat__pre-attach-remove'
            removeButton.setAttribute('aria-label', removeLabel)
            removeButton.dataset.action = 'click->litigant-messages--form-handler#removeFile'
            removeButton.dataset.index = String(index)

            const times = document.createElement('span')
            times.setAttribute('aria-hidden', 'true')
            times.textContent = '\u00d7'
            removeButton.appendChild(times)

            wrapper.appendChild(card)
            wrapper.appendChild(removeButton)
            fragment.appendChild(wrapper)
        })

        this.attachChipsTarget.replaceChildren(fragment)
    }
}
